import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── helpers ─────────────────────────────────────── */
const getDateLabel = (dateString, t) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return t('notes.today');
  if (diff <= 30) return t('notes.daysAgo', { count: diff });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
};

/* ════════════════════════════════════════════════════
   Notes — journal-style redesign
   ════════════════════════════════════════════════════ */
const Notes = () => {
  const { t, i18n } = useTranslation();
  const { user: contextUser, isPremium: contextIsPremium, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const userId = contextUser?.user_id || contextUser?.id;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showNewNote, setShowNewNote] = useState(false);

  /* ── API ────────────────────────────────────────── */
  const fetchNotes = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/notes/${userId}`);
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[Notes] fetch error', err);
      toast.error(t('notes.error'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
      if (selectedNote?.note_id === noteId) setSelectedNote(null);
      toast.success(t('notes.deleted'));
    } catch (err) {
      console.error('[Notes] delete error', err);
      toast.error(t('notes.error'));
    }
  };

  /* ── Effects ────────────────────────────────────── */
  useEffect(() => {
    if (userLoading || !userId) return;
    // Respect localStorage preference (source of truth) over server profile
    const savedLang = localStorage.getItem('betterhusbandUserLang');
    const lang = savedLang || contextUser?.language;
    if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading, userId, contextUser, contextIsPremium, i18n.language, fetchNotes]);

  useEffect(() => {
    setHeaderProps({
      title: t('notes.title'),
      user: contextUser,
      rightElement: (
        <button
          onClick={() => setShowNewNote(true)}
          className="w-6 h-6 rounded-full bg-[#191919] dark:bg-[#EFEFEF] text-white dark:text-[#18181C] flex items-center justify-center hover:bg-stone-800 dark:hover:bg-white transition-all active:scale-95"
        >
          <Icon icon="solar:add-circle-linear" width={14} />
        </button>
      ),
    });
  }, [t, contextUser, setHeaderProps, setShowNewNote]);

  /* ── Internal components ────────────────────────── */
  const NoteEditor = React.memo(function NoteEditor({
    initialTitle = '',
    initialContent = '',
    isNew = false,
    onSave,
    onCancel,
    noBg = false,
  }) {
    const textareaRef = useRef(null);
    const [title, setTitle] = useState(initialTitle);
    const [content, setContent] = useState(initialContent);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className={`${noBg ? 'bg-inherit border-none' : 'bg-white dark:bg-[#18181C] border border-stone-200 dark:border-[#2E2E36]'} rounded-2xl p-0 space-y-4`}
        style={{ fontFamily: 'Manrope-Variable', fontStyle: 'normal' }}
      >
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('notes.titlePlaceholder')}
          className="w-full text-2xl font-medium text-stone-900 dark:text-[#EFEFEF] placeholder:text-stone-300 dark:placeholder:text-[#3A3A3A] border-none rounded-full px-3 py-2 outline-none focus:ring-0 bg-inherit not-italic"
          autoFocus
          style={{ fontStyle: 'normal' }}
        />
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('notes.contentPlaceholder')}
          className="w-full min-h-[200px] not-italic text-stone-700 dark:text-[#EFEFEF] placeholder:text-stone-300 dark:placeholder:text-[#3A3A3A] border-none rounded-full px-3 py-2 outline-none bg-inherit resize-none leading-relaxed"
          style={{ fontStyle: 'normal' }}
        />
        <div className="flex items-center justify-between pt-4 border-t border-stone-100 dark:border-[#2E2E36]">
          <button onClick={onCancel} className="px-4 py-2 text-stone-500 dark:text-[#606060] hover:text-stone-700 dark:hover:text-[#EFEFEF] transition-colors uppercase text-sm">
            {t('notes.cancel')}
          </button>
          <button onClick={() => onSave(title, content)} className="px-6 py-2 bg-[#191919] text-white hover:bg-stone-800 transition-colors font-medium uppercase text-sm rounded-xl">
            {isNew ? t('notes.create') : t('notes.save')}
          </button>
        </div>
      </motion.div>
    );
  });

  /* ── Single journal entry row ───────────────────── */
  const JournalEntry = ({ note }) => (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      onClick={() => setSelectedNote(note)}
      className="w-full text-left p-4 mb-3 rounded-2xl flex flex-col gap-2 cursor-pointer group"
      style={{ background: 'var(--c-bg-secondary, #F5F0EB)' }}
    >
      {/* date label */}
      <span
        className="text-[10px] tracking-[0.18em] uppercase text-stone-400 dark:text-[#606060]"
        style={{ fontFamily: 'Manrope-Variable' }}
      >
        {getDateLabel(note.updated_at || note.created_at, t)}
      </span>

      {/* title */}
      <span className="text-[15px] text-stone-800 dark:text-[#EFEFEF] font-normal leading-snug" style={{ fontFamily: 'Manrope-Variable' }}>
        {note.title}
      </span>

      {/* italic quote */}
      {note.content && (
        <span
          className="text-[14px] italic text-stone-500 dark:text-[#808080] leading-relaxed line-clamp-2"
          style={{ fontFamily: 'Manrope-Variable' }}
        >
          "{note.content}"
        </span>
      )}
    </motion.div>
  );

  /* ── Note detail view ───────────────────────────── */
  const NoteDetail = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 pt-2 pb-10">
      {/* toolbar */}
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => setSelectedNote(null)} className="flex items-center gap-1 text-stone-500 dark:text-[#606060] hover:text-stone-700 dark:hover:text-[#EFEFEF] transition-colors text-sm">
          <Icon icon="solar:alt-arrow-left-linear" width={18} />
          {t('notes.back')}
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsEditing(true)} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors" style={{ background: 'var(--c-bg-secondary, #F5F0EB)' }}>
            <Icon icon="solar:pen-linear" width={16} className="text-stone-600 dark:text-[#909090]" />
          </button>
          <button onClick={() => handleDeleteNote(selectedNote.note_id)} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors" style={{ background: 'var(--c-bg-secondary, #F5F0EB)' }}>
            <Icon icon="solar:trash-bin-minimalistic-linear" width={16} className="text-red-500" />
          </button>
        </div>
      </div>

      {/* date */}
      <span className="text-[10px] tracking-[0.18em] uppercase text-stone-400 dark:text-[#606060] block mb-3" style={{ fontFamily: 'Manrope-Variable' }}>
        {getDateLabel(selectedNote.updated_at || selectedNote.created_at, t)}
      </span>

      {/* title */}
      <h1 className="text-2xl font-medium text-stone-900 dark:text-[#EFEFEF] mb-4" style={{ fontFamily: 'Manrope-Variable' }}>
        {selectedNote.title}
      </h1>

      {/* content */}
      <p className="text-stone-700 dark:text-[#EFEFEF] whitespace-pre-wrap leading-relaxed italic" style={{ fontFamily: 'Manrope-Variable', fontSize: 15 }}>
        {selectedNote.content || t('notes.noContent')}
      </p>
    </motion.div>
  );

  /* ── Render ─────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container" style={{ background: 'inherit' }}>
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit' }}>
      <AnimatePresence mode="wait">
        {/* ── Detail view ──────────────────────────── */}
        {selectedNote && !isEditing && <NoteDetail key="detail" />}

        {/* ── Edit modal ───────────────────────────── */}
        {selectedNote && isEditing && (
          <Modal key="edit" placement="bottom" onClose={() => setIsEditing(false)} ariaLabel={i18n.language?.startsWith('it') ? 'Modifica nota' : i18n.language?.startsWith('es') ? 'Editar nota' : 'Edit note'}>
            <motion.div initial={{ scale: 0.98, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.98, opacity: 0, y: 8 }} transition={{ duration: 0.16 }} className="w-[min(92%,520px)] max-w-md z-50">
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 32 }}>{i18n.language?.startsWith('it') ? 'Modifica nota' : i18n.language?.startsWith('es') ? 'Editar nota' : 'Edit note'}</div>
              <NoteEditor
                initialTitle={selectedNote.title || ''}
                initialContent={selectedNote.content || ''}
                onSave={async (title, content) => {
                  if (!title.trim()) { toast.error(t('notes.titleRequired')); return; }
                  try {
                    const res = await axios.put(`${API}/notes/${selectedNote.note_id}`, { title, content, tags: selectedNote.tags || [] });
                    setNotes((prev) => prev.map((n) => (n.note_id === selectedNote.note_id ? res.data : n)));
                    setIsEditing(false);
                    setSelectedNote(res.data);
                    toast.success(t('notes.updated'));
                  } catch (err) { console.error(err); toast.error(t('notes.error')); }
                }}
                onCancel={() => setIsEditing(false)}
                noBg
              />
            </motion.div>
          </Modal>
        )}

        {/* ── New note modal ───────────────────────── */}
        {showNewNote && (
          <Modal key="new" placement="bottom" onClose={() => setShowNewNote(false)} ariaLabel={t('notes.newNote')}>
            <motion.div initial={{ scale: 0.98, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.98, opacity: 0, y: 8 }} transition={{ duration: 0.16 }} className="w-[min(100%,520px)] max-w-md z-50 px-0">
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 32 }}>{t('notes.newReflection')}</div>
              <NoteEditor
                noBg
                isNew
                initialTitle=""
                initialContent=""
                onSave={async (title, content) => {
                  if (!title.trim()) { toast.error(t('notes.titleRequired')); return; }
                  try {
                    const res = await axios.post(`${API}/notes`, { user_id: userId, title, content, tags: [] });
                    setNotes((prev) => [res.data, ...prev]);
                    setShowNewNote(false);
                    toast.success(t('notes.created'));
                  } catch (err) { console.error(err); toast.error(t('notes.error')); }
                }}
                onCancel={() => setShowNewNote(false)}
              />
            </motion.div>
          </Modal>
        )}

        {/* ── Journal list ─────────────────────────── */}
        {!selectedNote && !showNewNote && (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 pt-8 pb-32">

            {/* entries */}
            {notes.length > 0 ? (
              notes.map((note) => <JournalEntry key={note.note_id} note={note} />)
            ) : (
              <div className="text-center">

                <p className="text-[15px] text-stone-400 dark:text-[#606060]" style={{ fontFamily: 'Manrope-Variable' }}>
                  {t('notes.emptyEncouragement')}
                </p>
              </div>
            )}

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Notes;
